class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);


/* ORIENTGEM_HOMEPAGE_HERO_FORCEFIX_20260331 */
(function(){
  function apply(){
    if(location.pathname !== '/' && location.pathname !== '/en') return;
    var hero = document.querySelector('.orientgem-home-hero');
    if(!hero) return;
    window.__ogHeroForceFixLoaded = true;
    document.documentElement.setAttribute('data-og-hero-forcefix','run');
    var media = hero.querySelector('.orientgem-home-hero__media');
    var img = hero.querySelector('.orientgem-home-hero__media img');
    var overlay = hero.querySelector('.orientgem-home-hero__overlay');
    var content = hero.querySelector('.orientgem-home-hero__content');
    var eyebrow = hero.querySelector('.orientgem-home-hero__eyebrow');
    var title = hero.querySelector('h1');
    var intro = hero.querySelector('.orientgem-home-hero__intro');
    var actions = hero.querySelector('.orientgem-home-hero__actions');
    var isMobile = window.innerWidth <= 749;
    var isDesktop = window.innerWidth >= 990;
    var isTablet = !isMobile && !isDesktop;
    function imp(el, prop, value){ if(el) el.style.setProperty(prop, value, 'important'); }
    if(media && overlay && media.nextElementSibling !== overlay){ media.insertAdjacentElement('afterend', overlay); }
    imp(hero,'display','block');
    imp(hero,'position','relative');
    imp(hero,'overflow','visible');
    imp(hero,'margin', isMobile ? '0 auto 12px' : '0 auto 18px');
    imp(hero,'padding', isMobile ? '0 12px 14px' : (isTablet ? '0 18px 18px' : '0 28px 28px'));
    if(img){
      imp(img,'display','block');
      imp(img,'width','100%');
      imp(img,'height', isMobile ? 'clamp(33rem, 122vw, 39rem)' : (isTablet ? 'clamp(40rem, 78vw, 52rem)' : 'clamp(42rem, 52vw, 52rem)'));
      imp(img,'min-height', isMobile ? '33rem' : (isTablet ? '40rem' : '42rem'));
      imp(img,'object-fit','cover');
      imp(img,'object-position', isMobile ? 'center 16%' : (isTablet ? 'center 12%' : 'center 10%'));
      imp(img,'border-radius', isMobile ? '1.3rem' : '1.6rem');
      imp(img,'background','#f5f1ea');
    }
    if(overlay){
      imp(overlay,'position','static');
      imp(overlay,'left','auto'); imp(overlay,'right','auto'); imp(overlay,'bottom','auto'); imp(overlay,'inset','auto');
      imp(overlay,'display','block');
      imp(overlay,'padding','0');
      imp(overlay,'background','none');
      imp(overlay,'border','0');
      imp(overlay,'box-shadow','none');
      imp(overlay,'backdrop-filter','none'); imp(overlay,'-webkit-backdrop-filter','none');
      imp(overlay,'max-width', isMobile ? 'calc(100% - 18px)' : (isTablet ? '31rem' : '54rem'));
      imp(overlay,'width', isMobile ? 'auto' : (isTablet ? 'auto' : 'min(54rem, calc(100% - 4rem))'));
      imp(overlay,'margin', isMobile ? '-1rem 9px 0' : (isTablet ? '-2.4rem auto 0 1.5rem' : '-6.5rem auto 0'));
      imp(overlay,'z-index','3');
    }
    if(content){
      imp(content,'max-width','none');
      imp(content,'color','#2c221c');
      imp(content,'background','linear-gradient(180deg,rgba(248,243,236,.97) 0%,rgba(241,233,223,.99) 100%)');
      imp(content,'border','1px solid rgba(120,95,68,.13)');
      imp(content,'border-radius', isMobile ? '1.18rem' : '1.55rem');
      imp(content,'box-shadow','0 1rem 2.5rem rgba(52,37,24,.10)');
      imp(content,'padding', isMobile ? '1rem 1rem 1.05rem' : (isTablet ? '1.15rem 1.2rem 1.2rem' : '1.8rem 2rem 1.95rem'));
    }
    if(eyebrow){ imp(eyebrow,'margin','0 0 .55rem'); imp(eyebrow,'font-size', isMobile ? '.66rem' : '.72rem'); imp(eyebrow,'letter-spacing','.18em'); imp(eyebrow,'color','rgba(97,76,52,.82)'); }
    if(title){ imp(title,'margin','0 0 .7rem'); imp(title,'font-size', isMobile ? '1.88rem' : (isTablet ? '2.45rem' : '3.85rem')); imp(title,'line-height', isMobile ? '1.02' : (isTablet ? '.98' : '.96')); imp(title,'max-width', isMobile ? '10ch' : 'none'); imp(title,'text-align', isDesktop ? 'center' : 'left'); imp(title,'color','#2a2019'); imp(title,'text-shadow','none'); }
    if(intro){ imp(intro,'margin', isDesktop ? '0 auto' : '0'); imp(intro,'max-width', isDesktop ? '48rem' : 'none'); imp(intro,'font-size', isMobile ? '.92rem' : (isDesktop ? '1.03rem' : '.98rem')); imp(intro,'line-height', isMobile ? '1.58' : '1.62'); imp(intro,'text-align', isDesktop ? 'center' : 'left'); imp(intro,'color','rgba(44,34,28,.84)'); imp(intro,'text-shadow','none'); }
    if(actions){ imp(actions,'display','grid'); imp(actions,'gap', isMobile ? '.62rem' : '.68rem'); imp(actions,'margin-top', isDesktop ? '1.15rem' : '.9rem'); imp(actions,'grid-template-columns', isDesktop ? 'repeat(2,minmax(0,1fr))' : '1fr'); imp(actions,'max-width', isDesktop ? '34rem' : 'none'); imp(actions,'margin-inline', isDesktop ? 'auto' : '0'); }
    hero.querySelectorAll('.orientgem-home-hero__actions .button').forEach(function(btn){ imp(btn,'width','100%'); imp(btn,'min-width','0'); imp(btn,'justify-content','center'); imp(btn,'background','#f7f2ea'); imp(btn,'color','#1e1a17'); imp(btn,'border-color','rgba(96,74,52,.22)'); imp(btn,'min-height', isMobile ? '3.45rem' : '3.25rem'); });
    hero.querySelectorAll('.orientgem-home-hero__actions .button--secondary').forEach(function(btn){ imp(btn,'background','rgba(255,255,255,.55)'); imp(btn,'color','#2d221b'); imp(btn,'border-color','rgba(96,74,52,.28)'); });
  }
  var timer;
  function queue(){ clearTimeout(timer); timer=setTimeout(apply,80); }
  document.addEventListener('DOMContentLoaded', queue);
  window.addEventListener('load', queue);
  window.addEventListener('pageshow', queue);
  window.addEventListener('resize', queue);
  [0,300,900,1800,3200,5200].forEach(function(ms){ setTimeout(apply, ms); });
  var mo = new MutationObserver(queue);
  document.addEventListener('DOMContentLoaded', function(){ var hero=document.querySelector('.orientgem-home-hero'); if(hero) mo.observe(hero,{attributes:true,subtree:true,attributeFilter:['style','class']}); });
})();
